/* eslint-disable max-lines */
import { action, computed, observable } from 'mobx';
import type { Moment } from 'moment';
import moment from 'moment';
import { toDomesticTel } from '@this/src/util';
import type {
  TravelerShinkansenTypeArgs,
  TravelerShinkansenTypeResponseArgs
} from './traveler/traveler_shinkansen_type';
import TravelerShinkansenType, {
  convertTravelerShinkansenTypeResponseToArgs
} from './traveler/traveler_shinkansen_type';
import type { UserResponseArgs } from './user/user2';
import User, { convertUserResponseToArgs } from './user/user2';
import type { MileageNumberResponseArgs } from './mileage_number2';
import MileageNumber, { convertMileageNumberResponseToArgs } from './mileage_number2';
import { convertFullWidthToHalfWidth } from './tel_number_utils';
import type { ExicCardResponseArgs } from './exic_card2';
import ExicCard, { convertExicCardResponseToArgs } from './exic_card2';
import type { BusinessEkinetAccountArgs } from './business_ekinet_account';
import BusinessEkinetAccount from './business_ekinet_account';

export type Gender = 'male' | 'female';

export const GENDER_LABELS: Record<Gender, string> = {
  male: '男性',
  female: '女性'
};

export type ShinkansenSeatType = 'free' | 'reserve' | 'green' | 'bulk_ticket' | 'ex' | 'gran_class';
export type ShinkansenTicketType = 'normal' | 'kyuusyuu' | 'bulk_ticket' | 'business_ekinet' | 'ex';

export type TravelerInformationResponseArgs = {
  id: number;
  name?: string;
  type?: 'companion' | 'self' | 'member';
  user_id?: number;
  birthday?: string;
  user_name?: string;
  last_name_kana?: string;
  first_name_kana?: string;
  last_name_roman?: string;
  middle_name_roman?: string;
  first_name_roman?: string;
  flight_last_name?: string;
  flight_middle_name?: string;
  flight_first_name?: string;
  flight_last_name_roman?: string;
  flight_middle_name_roman?: string;
  flight_first_name_roman?: string;
  flight_last_name_kana?: string;
  flight_first_name_kana?: string;
  flight_birthday?: string;
  flight_gender?: Gender;
  flight_tel?: string;
  air_seat?: string;
  mileage_number?: string;
  mileage_numbers?: MileageNumberResponseArgs[];
  passport_number?: string | null;
  passport_expire?: string | null;
  passport_issued_country?: string;
  shinkansen_seat?: string;
  shinkansen_seat_type?: ShinkansenSeatType;
  shinkansen_seat_type_text?: string;
  shinkansen_ticket_type?: ShinkansenTicketType;
  shinkansen_ticket_type_text?: string;
  show_railway_seat?: boolean;
  traveler_information_shinkansen_types?: TravelerShinkansenTypeResponseArgs[];
  exic_number?: string;
  exic_password?: string;
  exic_card_id?: number;
  business_ekinet_account_id?: number | null;
  business_ekinet_account?: BusinessEkinetAccountArgs;
  exic_card?: ExicCardResponseArgs;
  user?: UserResponseArgs;
  nationality_id?: number;
  immigration_card_creation?: boolean;
  visa_application?: boolean;
};

export type TravelerInformationArgs = {
  id?: number;
  name?: string;
  type?: 'companion' | 'self' | 'member';
  userId?: number;
  birthday?: Moment;
  userName?: string;
  lastNameKana?: string;
  firstNameKana?: string;
  lastNameRoman?: string;
  firstNameRoman?: string;
  middleNameRoman?: string;
  flightLastName?: string;
  flightMiddleName?: string;
  flightFirstName?: string;
  flightFirstNameRoman?: string;
  flightLastNameRoman?: string;
  flightMiddleNameRoman?: string;
  flightFirstNameKana?: string;
  flightLastNameKana?: string;
  flightBirthday?: Moment;
  flightGender?: Gender;
  flightTel?: string;
  airSeat?: string;
  mileageNumber?: string;
  mileageNumbers?: MileageNumber[];
  passportNumber?: string | null;
  passportExpire?: string | null;
  passportIssuedCountry?: string;
  shinkansenSeat?: string;
  shinkansenSeatInfo?: string;
  shinkansenSeatType?: ShinkansenSeatType;
  shinkansenSeatTypeText?: string;
  shinkansenTicketType?: ShinkansenTicketType;
  shinkansenTicketTypeText?: string;
  showRailwaySeat?: boolean;
  travelerInformationShinkansenTypes?: TravelerShinkansenTypeArgs[];
  exicNumber?: string;
  exicPassword?: string;
  exicCardId?: number;
  businessEkinetAccountId?: number | null;
  businessEkinetAccount?: BusinessEkinetAccount;
  exicCard?: ExicCard;
  user?: User;
  nationalityId?: number;
  immigrationCardCreation?: boolean;
  visaApplication?: boolean;
  exicPasswordType?: 'password' | 'text';
};

type ResolveFlightTelArgs = {
  type?: TravelerInformationArgs['type'];
  currentFlightTel?: string;
  responseFlightTel?: string;
  user?: User;
  preserveCurrent?: boolean;
};

export const resolveTravelerFlightTel = ({
  type,
  currentFlightTel,
  responseFlightTel,
  user,
  preserveCurrent = true
}: ResolveFlightTelArgs) => {
  if (preserveCurrent && currentFlightTel) {
    return currentFlightTel;
  }

  if (responseFlightTel) {
    return responseFlightTel;
  }

  if (type !== 'companion') {
    return user?.nationalizedTel || user?.tel || '';
  }

  return '';
};

export const convertTravelerInformationResponseToArgs = (response: TravelerInformationResponseArgs) => {
  const user = response.user ? new User(convertUserResponseToArgs(response.user)) : undefined;
  const args: TravelerInformationArgs = {
    id: response.id,
    name: response.name,
    type: response.type,
    userId: response.user_id,
    birthday: response.birthday
      ? moment(response.birthday)
      : response.flight_birthday
      ? moment(response.flight_birthday)
      : undefined,
    userName: response.user_name,
    lastNameKana: response.last_name_kana,
    firstNameKana: response.first_name_kana,
    lastNameRoman: response.last_name_roman,
    firstNameRoman: response.first_name_roman,
    middleNameRoman: response.middle_name_roman,
    flightLastName: response.flight_last_name,
    flightMiddleName: response.flight_middle_name,
    flightFirstName: response.flight_first_name,
    flightFirstNameRoman: response.flight_first_name_roman,
    flightLastNameRoman: response.flight_last_name_roman,
    flightMiddleNameRoman: response.flight_middle_name_roman,
    flightFirstNameKana: response.flight_first_name_kana,
    flightLastNameKana: response.flight_last_name_kana,
    flightBirthday: response.flight_birthday ? moment(response.flight_birthday) : undefined,
    flightGender: response.flight_gender,
    flightTel: resolveTravelerFlightTel({
      type: response.type,
      responseFlightTel: response.flight_tel,
      user
    }),
    airSeat: response.air_seat,
    mileageNumber: response.mileage_number,
    mileageNumbers:
      response.mileage_numbers?.map(
        mileageNumber => new MileageNumber(convertMileageNumberResponseToArgs(mileageNumber))
      ) || [],
    passportNumber: response.passport_number,
    passportExpire: response.passport_expire,
    passportIssuedCountry: response.passport_issued_country,
    shinkansenSeat: response.shinkansen_seat?.split('\n')[0],
    shinkansenSeatInfo: response.shinkansen_seat?.split('\n')[1],
    shinkansenSeatType: response.shinkansen_seat_type,
    shinkansenSeatTypeText: response.shinkansen_seat_type_text,
    shinkansenTicketType: response.shinkansen_ticket_type,
    shinkansenTicketTypeText: response.shinkansen_ticket_type_text,
    showRailwaySeat: response.show_railway_seat,
    travelerInformationShinkansenTypes:
      response.traveler_information_shinkansen_types?.map(type => {
        return convertTravelerShinkansenTypeResponseToArgs(type);
      }) || [],
    exicNumber: response.exic_number,
    exicPassword: response.exic_password,
    exicCardId: response.exic_card_id,
    businessEkinetAccountId: response.business_ekinet_account_id,
    businessEkinetAccount: response.business_ekinet_account
      ? new BusinessEkinetAccount(response.business_ekinet_account)
      : undefined,
    exicCard: response.exic_card ? new ExicCard(convertExicCardResponseToArgs(response.exic_card)) : undefined,
    user,
    nationalityId: response.nationality_id,
    immigrationCardCreation: response.immigration_card_creation,
    visaApplication: response.visa_application
  };
  return args;
};

export default class TravelerInformation {
  @observable id?: number;
  @observable name?: string;
  @observable type?: 'companion' | 'self' | 'member';
  @observable userId?: number;
  @observable birthday?: Moment;
  @observable userName?: string;
  @observable lastNameKana?: string;
  @observable firstNameKana?: string;
  @observable lastNameRoman?: string;
  @observable firstNameRoman?: string;
  @observable middleNameRoman?: string;
  @observable flightLastName?: string;
  @observable flightMiddleName?: string;
  @observable flightFirstName?: string;
  @observable flightFirstNameRoman?: string;
  @observable flightLastNameRoman?: string;
  @observable flightMiddleNameRoman?: string;
  @observable flightFirstNameKana?: string;
  @observable flightLastNameKana?: string;
  @observable flightBirthday?: Moment;
  @observable flightGender?: Gender;
  @observable flightTel?: string;
  @observable airSeat?: string;
  @observable mileageNumber?: string;
  @observable mileageNumbers: MileageNumber[];
  @observable passportNumber?: string | null;
  @observable passportExpire?: string | null;
  @observable passportIssuedCountry?: string;
  @observable shinkansenSeat?: string;
  @observable shinkansenSeatInfo?: string;
  @observable shinkansenSeatType?: ShinkansenSeatType;
  @observable shinkansenSeatTypeText?: string;
  @observable shinkansenTicketType?: ShinkansenTicketType;
  @observable shinkansenTicketTypeText?: string;
  @observable showRailwaySeat?: boolean;
  @observable travelerInformationShinkansenTypes: TravelerShinkansenType[];
  @observable exicNumber?: string;
  @observable exicPassword?: string;
  @observable exicCardId?: number;
  @observable businessEkinetAccountId?: number | null;
  @observable businessEkinetAccount?: BusinessEkinetAccount;
  @observable exicCard?: ExicCard;
  @observable user?: User;
  @observable nationalityId?: number;
  @observable immigrationCardCreation?: boolean;
  @observable visaApplication?: boolean;
  @observable exicPasswordType: 'password' | 'text';

  constructor(args: TravelerInformationArgs) {
    this.id = args.id;
    this.name = args.name;
    this.type = args.type;
    this.userId = args.userId;
    this.birthday = args.birthday;
    this.userName = args.userName;
    this.lastNameKana = args.lastNameKana;
    this.firstNameKana = args.firstNameKana;
    this.lastNameRoman = args.lastNameRoman;
    this.firstNameRoman = args.firstNameRoman;
    this.middleNameRoman = args.middleNameRoman;
    this.flightLastName = args.flightLastName;
    this.flightMiddleName = args.flightMiddleName;
    this.flightFirstName = args.flightFirstName;
    this.flightFirstNameRoman = args.flightFirstNameRoman;
    this.flightLastNameRoman = args.flightLastNameRoman;
    this.flightMiddleNameRoman = args.flightMiddleNameRoman;
    this.flightFirstNameKana = args.flightFirstNameKana;
    this.flightLastNameKana = args.flightLastNameKana;
    this.flightBirthday = args.flightBirthday;
    this.flightGender = args.flightGender;
    this.flightTel = args.flightTel;
    this.airSeat = args.airSeat;
    this.mileageNumber = args.mileageNumber;
    this.mileageNumbers = args.mileageNumbers || [];
    this.passportNumber = args.passportNumber;
    this.passportExpire = args.passportExpire;
    this.passportIssuedCountry = args.passportIssuedCountry;
    this.shinkansenSeat = args.shinkansenSeat;
    this.shinkansenSeatInfo = args.shinkansenSeatInfo;
    this.shinkansenSeatType = args.shinkansenSeatType;
    this.shinkansenSeatTypeText = args.shinkansenSeatTypeText;
    this.shinkansenTicketType = args.shinkansenTicketType;
    this.shinkansenTicketTypeText = args.shinkansenTicketTypeText;
    this.showRailwaySeat = args.showRailwaySeat;
    this.travelerInformationShinkansenTypes =
      args.travelerInformationShinkansenTypes?.map(type => new TravelerShinkansenType(type)) || [];
    this.exicNumber = args.exicNumber;
    this.exicPassword = args.exicPassword;
    this.exicCardId = args.exicCardId;
    this.businessEkinetAccountId = args.businessEkinetAccountId;
    this.businessEkinetAccount = args.businessEkinetAccount;
    this.exicCard = args.exicCard;
    this.user = args.user;
    this.nationalityId = args.nationalityId;
    this.immigrationCardCreation = args.immigrationCardCreation;
    this.visaApplication = args.visaApplication;
    this.exicPasswordType = args.exicPasswordType || 'password';
  }

  get travelerName() {
    if (this.userName) {
      return this.userName;
    }

    if (this.lastNameKana && this.firstNameKana) {
      return `${this.lastNameKana} ${this.firstNameKana}`;
    }

    if (this.lastNameRoman && this.firstNameRoman) {
      return `${this.lastNameRoman} ${this.firstNameRoman}`;
    }

    return '';
  }

  get updateParams() {
    return {
      id: this.id?.toString().match(/^temp-.*/) ? null : this.id,
      id_original: this.id,
      air_seat: this.airSeat,
      birthday: this.birthday?.format('YYYY-MM-DD'),
      first_name_kana: this.firstNameKana,
      last_name_kana: this.lastNameKana,
      first_name_roman: this.firstNameRoman,
      last_name_roman: this.lastNameRoman,
      flight_first_name: this.flightFirstName,
      flight_last_name: this.flightLastName,
      flight_middle_name: this.flightMiddleName,
      flight_first_name_kana: this.flightFirstNameKana,
      flight_last_name_kana: this.flightLastNameKana,
      flight_first_name_roman: this.flightFirstNameRoman,
      flight_last_name_roman: this.flightLastNameRoman,
      flight_middle_name_roman: this.flightMiddleNameRoman,
      flight_birthday: this.flightBirthday?.format('YYYY-MM-DD'),
      flight_gender: this.flightGender,
      flight_tel: this.flightTel,
      middle_name_roman: this.middleNameRoman,
      mileage_number: this.mileageNumber,
      passport_expire: this.passportExpire,
      passport_issued_country: this.passportIssuedCountry,
      passport_number: this.passportNumber,
      shinkansen_seat: this.shinkansenSeat ?? '指定なし',
      shinkansen_seat_type: this.shinkansenSeatType,
      shinkansen_ticket_type: this.shinkansenTicketType,
      exic_number: this.exicNumber,
      exic_password: this.exicPassword,
      exic_card_id: this.exicCardId,
      user_id: this.userId,
      traveler_information_shinkansen_types: this.travelerInformationShinkansenTypes.map(
        type => type.updateParams
      ),
      nationality_id: this.nationalityId
    };
  }

  @computed get travelerMileageNumberDomestic() {
    const list: string[] = [];

    // 社員マスタから選択した(userIdを持っている)場合、mileageNumber(手入力)は表示しない
    if (this.mileageNumber && !this.userId) {
      list.push(this.mileageNumber);
    }

    this.mileageNumbers?.forEach(mileageNumber => {
      list.push(`(${mileageNumber.carrier}) ${mileageNumber.number}`);
    });

    return list.join('、');
  }

  @computed get travelerMileageNumberForeign() {
    const list: string[] = [];

    if (this.mileageNumber) {
      list.push(this.mileageNumber);
    }

    this.mileageNumbers?.forEach(mileageNumber => {
      list.push(`(${mileageNumber.carrier}) ${mileageNumber.number}`);
    });

    return list.join('、');
  }

  @computed get travelerAirlineTypeDomestic() {
    const list: string[] = [];

    if (this.airSeat) {
      list.push(`座席希望: ${this.airSeat}`);
    }

    if ((this.mileageNumber && !this.userId) || this.mileageNumbers.length > 0) {
      list.push(`マイル番号: ${this.travelerMileageNumberDomestic}`);
    }

    return `(${this.travelerName}) ${list.join(', ')}`;
  }

  @computed get travelerAirlineTypeForeign() {
    const list: string[] = [];

    if (this.airSeat) {
      list.push(`座席希望: ${this.airSeat}`);
    }

    if (this.mileageNumber || this.mileageNumbers.length > 0) {
      list.push(`マイル番号: ${this.travelerMileageNumberForeign}`);
    }

    return `(${this.travelerName}) ${list.join(', ')}`;
  }

  getShinkansenSeatTypeText(elementIndex: number | null = null) {
    if (
      elementIndex != null &&
      this.travelerInformationShinkansenTypes &&
      this.travelerInformationShinkansenTypes[elementIndex]
    ) {
      const type = this.travelerInformationShinkansenTypes[elementIndex];
      return type.getShinkansenSeatTypeText() || '';
    }

    if (this.shinkansenSeatTypeText) {
      return this.shinkansenSeatTypeText;
    }

    switch (this.shinkansenSeatType) {
      case 'free':
        return '自由席';
      case 'reserve':
        return '指定席';
      case 'green':
        return 'グリーン席';
      default:
        return '';
    }
  }

  getShinkansenTicketTypeText(elementIndex: number | null = null) {
    if (
      elementIndex != null &&
      this.travelerInformationShinkansenTypes &&
      this.travelerInformationShinkansenTypes[elementIndex]
    ) {
      const type = this.travelerInformationShinkansenTypes[elementIndex];
      return type.getShinkansenTicketTypeText() || '';
    }

    if (this.shinkansenTicketTypeText) {
      return this.shinkansenTicketTypeText;
    }

    switch (this.shinkansenTicketType) {
      case 'normal':
        return '通常きっぷ';
      case 'kyuusyuu':
        return 'JR九州きっぷ';
      case 'bulk_ticket':
        return '回数券';
      case 'business_ekinet':
        return 'ビジネスえきねっと';
      case 'ex':
        return 'EX-IC';
      default:
        return '';
    }
  }

  @action assignFromUser(user: User) {
    this.userId = user.id;
    this.userName = user.name;
    this.birthday = user.birthday;
    this.lastNameKana = user.lastNameKana;
    this.firstNameKana = user.firstNameKana;
    this.lastNameRoman = user.lastNameRoman;
    this.firstNameRoman = user.firstNameRoman;
    this.middleNameRoman = '';
    this.flightLastName = user.lastName;
    this.flightMiddleName = '';
    this.flightFirstName = user.firstName;
    this.flightFirstNameRoman = user.firstNameRoman;
    this.flightLastNameRoman = user.lastNameRoman;
    this.flightMiddleNameRoman = '';
    this.flightFirstNameKana = user.firstNameKana;
    this.flightLastNameKana = user.lastNameKana;
    this.flightBirthday = user.birthday;
    this.flightGender = user.gender as Gender;
    this.flightTel = resolveTravelerFlightTel({
      type: this.type,
      user,
      preserveCurrent: false
    });
    this.mileageNumber = '';
    this.mileageNumbers = user.mileageNumbers || [];
    this.passportNumber = user.passportNumber;
    this.passportExpire = user.passportExpire;
    this.passportIssuedCountry = '日本';
    this.shinkansenSeat = '';
    this.shinkansenSeatInfo = '';
    this.shinkansenSeatType = undefined;
    this.shinkansenSeatTypeText = '';
    this.shinkansenTicketType = undefined;
    this.shinkansenTicketTypeText = '';
    this.showRailwaySeat = false;
    this.travelerInformationShinkansenTypes = [];
    this.exicPasswordType = 'password';
    this.user = user;
  }

  @action setFlightBirthday(value: Moment) {
    this.flightBirthday = value;
    this.birthday = value;
  }

  travelerShinkansenType(elementIndex: number | null = null) {
    const list: string[] = [];

    if (this.shinkansenTicketType) {
      list.push(`券種: ${this.getShinkansenTicketTypeText(elementIndex)}`);
    }

    if (this.shinkansenSeatType) {
      list.push(`座席: ${this.getShinkansenSeatTypeText(elementIndex)}`);
    }

    if (this.shinkansenSeat) {
      list.push(`座席希望: ${this.shinkansenSeat}`);
    }

    if (this.exicNumber && this.shinkansenTicketType === 'ex') {
      list.push(`EXIC会員ID: ${this.exicNumber}`);
    }

    if (this.exicPassword && this.shinkansenTicketType === 'ex') {
      list.push(`EXICパスワード: ${this.exicPassword.replace(/./g, '*')}`);
    }

    return `(${this.travelerName}) ${list.join(', ')}`;
  }

  validate(errors: { [key: string]: string | undefined }, isForeignFlightCustomerInfoRequired: boolean) {
    switch (this.type) {
      case 'self':
        if (!this.id && !this.userId && !this.user?.id) {
          errors.memberSelect = '出張者を選択してください';
        }
        break;
      case 'member':
        if (!this.id && !this.userId && !this.user?.id) {
          errors.memberSelect = '出張者を選択してください';
        }
        break;
      case 'companion':
        if (!errors.travelerFirstNameRomen && !errors.travelerLastNameRomen) {
          errors.travelerFirstNameRomen = this.requiredError('出張者の名前(ローマ字)', this.firstNameRoman);
          errors.travelerLastNameRomen = this.requiredError('出張者の名字(ローマ字)', this.lastNameRoman);
        }
        if (!errors.travelerFirstNameKana && !errors.travelerLastNameKana) {
          errors.travelerFirstNameKana = this.requiredError('出張者の名前(カナ)', this.firstNameKana);
          errors.travelerLastNameKana = this.requiredError('出張者の名字(カナ)', this.lastNameKana);
        }
        if (!errors.travelerFirstNameRomen && !errors.travelerLastNameRomen) {
          errors.travelerFirstNameRomen = this.alphabetError('出張者の名前(ローマ字)', this.firstNameRoman);
          errors.travelerLastNameRomen = this.alphabetError('出張者の名字(ローマ字)', this.lastNameRoman);
        }
        if (!errors.travelerFirstNameKana && !errors.travelerLastNameKana) {
          errors.travelerFirstNameKana = this.katakanaError('出張者の名前(カナ)', this.firstNameKana);
          errors.travelerLastNameKana = this.katakanaError('出張者の名字(カナ)', this.lastNameKana);
        }
        if (!isForeignFlightCustomerInfoRequired) {
          if (!errors.travelerFirstNameKana && !errors.travelerLastNameKana) {
            errors.travelerFirstName =
              this.katakanaError('搭乗者氏名', this.flightLastNameKana) ||
              this.katakanaError('搭乗者氏名', this.flightFirstNameKana);
          }
        }
        break;
      default:
        break;
    }
  }

  flightValidate(errors: { [key: string]: string | undefined }, isForeignFlightCustomerInfoRequired: boolean) {
    if (isForeignFlightCustomerInfoRequired) {
      errors.flightCustomerName =
        this.alphabetError('搭乗者氏名', this.flightLastNameRoman) ||
        this.alphabetError('搭乗者氏名', this.flightFirstNameRoman) ||
        this.alphabetErrorAllowEmpty('搭乗者氏名', this.flightMiddleNameRoman);
    } else {
      errors.flightCustomerName =
        this.requiredError('搭乗者氏名', this.flightLastNameKana) ||
        this.requiredError('搭乗者氏名', this.flightFirstNameKana);
      if (!errors.flightCustomerName) {
        errors.flightCustomerName =
          this.katakanaError('搭乗者氏名', this.flightLastNameKana) ||
          this.katakanaError('搭乗者氏名', this.flightFirstNameKana);
      }
    }
    if (this.type === 'companion') {
      errors.travelerBirthday = this.dateError('出張者の誕生日', this.flightBirthday);
    }
    errors.flightCustomerBirthday = this.dateError('搭乗者の誕生日', this.flightBirthday);
    errors.flightCustomerTel = this.requiredError('搭乗者の緊急連絡先', toDomesticTel(this.flightTel || ''));
    if (isForeignFlightCustomerInfoRequired) {
      errors.passportNumber = this.requiredError('パスポート番号', this.passportNumber ?? undefined);
      errors.passportExpire = this.requiredError('パスポート有効期限', this.passportExpire ?? undefined);
      errors.passportIssuedCoutry = this.requiredError('パスポート発行国', this.passportIssuedCountry);
    }
    errors.flightCustomerGender = this.requiredError('性別', this.flightGender, 'を選択してください');
  }

  getTravelerParams(
    isForeignFlightCustomerInfoRequired: boolean,
    options?: { isShinkansenInfoRequired?: boolean; isHotelInfoRequired?: boolean }
  ) {
    const isOwnTraveler = !!this.user && !!this.userId && this.userId === this.user.id;
    const baseIdentityUser = options?.isHotelInfoRequired && isOwnTraveler ? this.user : undefined;
    const birthday = this.birthday ?? this.user?.birthday ?? this.flightBirthday;
    const tel =
      (isOwnTraveler ? this.user?.nationalizedTel ?? this.user?.tel : undefined) ??
      baseIdentityUser?.nationalizedTel ??
      baseIdentityUser?.tel ??
      this.user?.nationalizedTel ??
      this.user?.tel ??
      this.flightTel ??
      '';
    const resolvedFlightTel = resolveTravelerFlightTel({
      type: this.type,
      currentFlightTel: this.flightTel,
      user: isOwnTraveler ? this.user : baseIdentityUser || this.user
    });
    const isShinkansenInfoRequired = options?.isShinkansenInfoRequired ?? false;
    const isHotelInfoRequired = options?.isHotelInfoRequired ?? false;
    const shinkansenTicketType = isShinkansenInfoRequired
      ? this.shinkansenTicketType
      : this.shinkansenTicketType === 'ex'
      ? 'ex'
      : this.shinkansenTicketType === 'bulk_ticket'
      ? 'bulk_ticket'
      : 'normal';
    const shinkansenSeat = isShinkansenInfoRequired
      ? this.shinkansenSeatInfo
        ? `${this.shinkansenSeat || '指定なし'}:${this.shinkansenSeatInfo}`
        : this.shinkansenSeat || '指定なし'
      : this.shinkansenSeatInfo
      ? `${this.shinkansenSeat || '指定なし'}:${this.shinkansenSeatInfo}`
      : this.shinkansenSeat || '指定なし';
    const shouldClearExicCredentials =
      isShinkansenInfoRequired &&
      this.user?.organization?.exicUsageMode === 'card_based' &&
      this.type !== 'companion';
    const exicNumber = shouldClearExicCredentials
      ? ''
      : isShinkansenInfoRequired || isHotelInfoRequired
      ? this.exicNumber || ''
      : '';
    const exicPassword = shouldClearExicCredentials
      ? ''
      : isShinkansenInfoRequired || isHotelInfoRequired
      ? this.exicPassword || ''
      : '';
    const exicCardId = isShinkansenInfoRequired
      ? this.exicCardId ?? this.user?.exicCardId ?? null
      : this.user?.exicCardId ?? null;
    const passportExpire =
      isHotelInfoRequired && isOwnTraveler
        ? this.user?.passportExpire || this.passportExpire || ''
        : this.passportExpire || '';
    const firstNameKana = baseIdentityUser?.firstNameKana || this.firstNameKana;
    const lastNameKana = baseIdentityUser?.lastNameKana || this.lastNameKana;
    const firstNameRoman = baseIdentityUser?.firstNameRoman || this.firstNameRoman;
    const lastNameRoman = baseIdentityUser?.lastNameRoman || this.lastNameRoman;
    const flightFirstNameKana = this.flightFirstNameKana;
    const flightLastNameKana = this.flightLastNameKana;
    const flightFirstNameRoman = this.flightFirstNameRoman;
    const flightLastNameRoman = this.flightLastNameRoman;
    const shinkansenSeatType =
      this.shinkansenSeatType || (isHotelInfoRequired && isOwnTraveler ? 'reserve' : this.shinkansenSeatType);

    return {
      user_id: this.userId || (this.type === 'companion' ? '' : this.id) || '',
      type: this.type,
      first_name: this.user?.firstName,
      last_name: this.user?.lastName,
      first_name_kana: firstNameKana,
      last_name_kana: lastNameKana,
      first_name_roman: firstNameRoman,
      last_name_roman: lastNameRoman,
      birthday: birthday?.format('YYYY-MM-DD'),
      tel,
      air_seat: this.airSeat || '',
      shinkansen_ticket_type: shinkansenTicketType,
      shinkansen_seat_type: shinkansenSeatType,
      shinkansen_seat: shinkansenSeat,
      mileage_number: this.mileageNumber || '',
      exic_number: exicNumber,
      exic_password: exicPassword,
      exic_card_id: exicCardId,
      flight_first_name: isForeignFlightCustomerInfoRequired ? flightFirstNameRoman : flightFirstNameKana,
      flight_last_name: isForeignFlightCustomerInfoRequired ? flightLastNameRoman : flightLastNameKana,
      flight_middle_name: isForeignFlightCustomerInfoRequired ? this.flightMiddleNameRoman || '' : '',
      flight_first_name_kana: flightFirstNameKana,
      flight_last_name_kana: flightLastNameKana,
      flight_first_name_roman: flightFirstNameRoman,
      flight_last_name_roman: flightLastNameRoman,
      flight_middle_name_roman: this.flightMiddleNameRoman || '',
      flight_birthday: this.flightBirthday?.format('YYYY-MM-DD'),
      flight_gender: this.flightGender,
      flight_tel: resolvedFlightTel
        ? toDomesticTel(resolvedFlightTel.replace(/[-－﹣−‐⁃‑‒–—﹘―⎯⏤ーｰ─━]/g, ''))
        : '',
      passport_number: this.passportNumber || '',
      passport_expire: passportExpire,
      passport_issued_country: this.passportIssuedCountry || '日本',
      immigration_card_creation: this.immigrationCardCreation ?? false,
      visa_application: this.visaApplication ?? false,
      prefecture_id: 0,
      nationality_id: this.nationalityId ?? 121,
      traveler_information_id: this.id
    };
  }

  @action convertFlightTel() {
    const convertedValue = convertFullWidthToHalfWidth(this.flightTel || '');
    if (this.flightTel !== convertedValue) {
      this.flightTel = convertedValue;
    }
  }

  private requiredError(name: string, value?: string, suffix = 'を入力してください') {
    if (!value || value.length === 0) {
      return `${name}${suffix}`;
    }
    return undefined;
  }

  private katakanaError(name: string, value?: string) {
    if (!value || !value.match(/^[ァ-ヶー　]*$/)) {
      return `${name}はカタカナで入力してください`;
    }
    return undefined;
  }

  private alphabetError(name: string, value?: string) {
    const error = this.requiredError(name, value);
    if (error) return error;
    if (!value || !value.match(/^[a-zA-Z]+$/)) {
      return `${name}はアルファベッドで入力してください`;
    }
    return undefined;
  }

  private alphabetErrorAllowEmpty(name: string, value?: string) {
    if (!value || value.length === 0) {
      return undefined;
    }
    if (!value.match(/^[a-zA-Z]+$/)) {
      return `${name}はアルファベッドで入力してください`;
    }
    return undefined;
  }

  private dateError(name: string, value?: Moment) {
    const error = this.requiredError(name, value ? value.format('YYYY-MM-DD') : undefined);
    if (error) return error;
    if (!value || !moment(value).isValid()) {
      return `${name}は日付で入力してください`;
    }
    return undefined;
  }
}

export const buildTravelerParams = (
  travelers: TravelerInformation[],
  isForeignFlightCustomerInfoRequired: boolean,
  options?: { isShinkansenInfoRequired?: boolean; isHotelInfoRequired?: boolean }
) => travelers.map(traveler => traveler.getTravelerParams(isForeignFlightCustomerInfoRequired, options));
